import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Database, Loader2, Filter, ChevronDown, ChevronRight, RefreshCw, FileText, Layers, Upload, CheckCircle2, XCircle, Clock, Trash2 } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';
import { useAuthStore } from '../store/authStore';
import Swal from 'sweetalert2';

/**
 * Pinecone Data
 * Internal FK team view: browse the metadata of the vectors indexed in Pinecone (admin only)
 */

// Vector ID prefixes written by the different ingestion paths
const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'chunk_', label: 'Books (PDF chunks)' },
  { value: 'pdfa-', label: 'PDF analyses' },
  { value: 'gen-', label: 'Generated content' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

interface PineconeMetadata {
  pdfName?: string;
  fileName?: string;
  source?: string;
  sourceSystem?: string;
  oeCodes?: string[];
  topic?: string;
  chunkIndex?: number;
  totalChunks?: number;
  textPreview?: string;
  textLength?: number;
  [key: string]: unknown;
}

interface PineconeItem {
  id: string;
  metadata: PineconeMetadata;
}

const getSourceLabel = (id: string): string =>
  SOURCE_OPTIONS.find(option => option.value && id.startsWith(option.value))?.label || 'Other';

function VectorsView() {
  const { accessToken } = useAuthStore();
  const [prefix, setPrefix] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);
  const [items, setItems] = useState<PineconeItem[]>([]);
  const [indexName, setIndexName] = useState('');
  const [total, setTotal] = useState(0);
  // Pinecone pagination is token based: tokens[i] is the token that loads page i + 1
  const [tokens, setTokens] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(1);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(async (targetPage: number, pageTokens: (string | undefined)[]) => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: pageSize };
      if (prefix) params.prefix = prefix;
      const token = pageTokens[targetPage - 1];
      if (token) params.token = token;

      const response = await axios.get(`${API_BASE_URL}/chatbot/pinecone-metadata`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });
      const data = response.data.data;
      const next: string | undefined = data.pagination?.nextToken;

      setItems(data.items || []);
      setIndexName(data.indexName || '');
      setTotal(data.pagination?.total || 0);
      setNextToken(next);
      setPage(targetPage);
      setExpandedId(null);

      const updatedTokens = pageTokens.slice(0, targetPage);
      updatedTokens[targetPage] = next;
      setTokens(updatedTokens);
    } catch (err: any) {
      console.error('[PineconeData] Error loading Pinecone metadata:', err);
      setError(
        err.response?.status === 403
          ? 'You do not have access to Pinecone data.'
          : err.response?.data?.error || 'Failed to load Pinecone data.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, prefix, pageSize]);

  // Reload from the first page whenever the filters change
  useEffect(() => {
    loadPage(1, [undefined]);
  }, [loadPage]);

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Source</label>
            <select
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              {SOURCE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              disabled={isLoading}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Index</p>
          <p className="text-sm font-bold text-gray-800 break-all">{indexName || '—'}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Total Vectors (whole index)</p>
          <p className="text-2xl font-bold text-gray-800">{total.toLocaleString()}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">{error}</div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading Pinecone data...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Database className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No vectors found for the selected source.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">OE Codes</th>
                  <th className="px-4 py-3">Topic</th>
                  <th className="px-4 py-3">Chunk</th>
                  <th className="px-4 py-3">Text Preview</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const { metadata } = item;
                  const isExpanded = expandedId === item.id;
                  const fileName = metadata.pdfName || metadata.fileName || metadata.source;
                  const oeCodes = Array.isArray(metadata.oeCodes) ? metadata.oeCodes : [];
                  return (
                    <Fragment key={item.id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : item.id)}
                        className="border-b border-gray-100 hover:bg-gray-50 align-top cursor-pointer"
                      >
                        <td className="px-4 py-3 text-gray-400">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-700">
                            {getSourceLabel(item.id)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-800 max-w-[260px]">
                          {fileName ? <p className="line-clamp-2 break-all" title={fileName}>{fileName}</p> : '—'}
                        </td>
                        <td className="px-4 py-3 max-w-[180px]">
                          <div className="flex flex-wrap gap-1">
                            {oeCodes.length === 0 && <span className="text-gray-400">—</span>}
                            {oeCodes.map(code => (
                              <span
                                key={code}
                                title={code}
                                className="inline-flex px-2 py-0.5 rounded-md bg-orange-50 border border-orange-200 text-xs text-orange-700"
                              >
                                {/* Generated content codes are "<oe>@@<projectId>" - show the OE part only */}
                                {code.split('@@')[0]}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[220px]">
                          {metadata.topic ? <p className="line-clamp-2" title={metadata.topic}>{metadata.topic}</p> : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {typeof metadata.chunkIndex === 'number'
                            ? `${metadata.chunkIndex + 1}${metadata.totalChunks ? ` / ${metadata.totalChunks}` : ''}`
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[320px]">
                          <p className="line-clamp-3">{metadata.textPreview || '—'}</p>
                          {typeof metadata.textLength === 'number' && (
                            <div className="text-xs text-gray-400 mt-1">{metadata.textLength.toLocaleString()} chars</div>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <td colSpan={7} className="px-4 py-3">
                            <p className="text-xs text-gray-500 mb-2">
                              Vector ID: <span className="font-mono text-gray-700 break-all">{item.id}</span>
                            </p>
                            <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                              {JSON.stringify(metadata, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!error && !isLoading && items.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {items.length} vectors on this page
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadPage(page - 1, tokens)}
                disabled={page <= 1 || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page}</span>
              <button
                onClick={() => loadPage(page + 1, tokens)}
                disabled={!nextToken || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Pipeline run by the backend for every uploaded PDF (same order as the CLI scripts)
const INGEST_STEPS: { name: string; label: string }[] = [
  { name: 'pdf:to-json', label: 'Extract text' },
  { name: 'structure:openai', label: 'Structure with OpenAI' },
  { name: 'upload:pinecone', label: 'Upload to Pinecone' },
];

interface IngestStep {
  name: string;
  status: 'completed' | 'failed' | 'skipped';
  durationMs: number;
  detail?: string;
}

interface UploadJob {
  id: string;
  file: File;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  steps: IngestStep[];
  chunkCount?: number;
  replacedExisting?: boolean;
  error?: string;
}

function UploadPanel({ onUploaded }: { onUploaded: () => void }) {
  const { accessToken } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const updateJob = (id: string, patch: Partial<UploadJob>) =>
    setJobs(current => current.map(job => (job.id === id ? { ...job, ...patch } : job)));

  const handleFilesSelected = async (fileList: FileList | null) => {
    const files = Array.from(fileList || []).filter(file => file.name.toLowerCase().endsWith('.pdf'));
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0 || !accessToken) return;

    const newJobs: UploadJob[] = files.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      status: 'queued',
      steps: [],
    }));
    setJobs(current => [...newJobs, ...current]);
    setIsUploading(true);

    let uploadedAny = false;
    // One PDF at a time - every file runs the whole pipeline on the backend
    for (const job of newJobs) {
      updateJob(job.id, { status: 'processing' });
      try {
        const formData = new FormData();
        formData.append('file', job.file);
        const response = await axios.post(`${API_BASE_URL}/chatbot/pinecone-documents/upload`, formData, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = response.data.data;
        updateJob(job.id, {
          status: 'completed',
          steps: data.steps || [],
          chunkCount: data.chunkCount,
          replacedExisting: data.replacedExisting,
        });
        uploadedAny = true;
      } catch (err: any) {
        console.error('[PineconeData] Upload failed:', err);
        updateJob(job.id, {
          status: 'failed',
          steps: err.response?.data?.data?.steps || [],
          error: err.response?.data?.error || 'Upload failed.',
        });
      }
    }

    setIsUploading(false);
    if (uploadedAny) onUploaded();
  };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Upload Documents</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Each PDF runs: Extract text → Structure with OpenAI → Upload to Pinecone. Uploading a file name that is already indexed replaces it.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="px-6 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          Upload PDF
        </button>
      </div>

      {jobs.length > 0 && (
        <div className="mt-4 space-y-2">
          {jobs.map(job => (
            <div key={job.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-800 break-all">{job.file.name}</p>
                {job.status === 'queued' && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                    <Clock className="w-3.5 h-3.5" /> Queued
                  </span>
                )}
                {job.status === 'processing' && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#1e73be] whitespace-nowrap">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...
                  </span>
                )}
                {job.status === 'completed' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 border border-green-200 text-xs text-green-700 whitespace-nowrap">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {job.chunkCount} chunks {job.replacedExisting ? 'replaced' : 'indexed'}
                  </span>
                )}
                {job.status === 'failed' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 border border-red-200 text-xs text-red-700 whitespace-nowrap">
                    <XCircle className="w-3.5 h-3.5" /> Failed
                  </span>
                )}
              </div>

              {job.steps.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {INGEST_STEPS.map((stepDef, index) => {
                    const step = job.steps.find(s => s.name === stepDef.name);
                    const color =
                      step?.status === 'completed' ? 'bg-green-50 border-green-200 text-green-700'
                        : step?.status === 'failed' ? 'bg-red-50 border-red-200 text-red-700'
                          : 'bg-gray-50 border-gray-200 text-gray-400';
                    return (
                      <span
                        key={stepDef.name}
                        title={step?.detail}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs ${color}`}
                      >
                        {index + 1}. {stepDef.label}
                        {step?.status === 'completed' && ` · ${(step.durationMs / 1000).toFixed(1)}s`}
                      </span>
                    );
                  })}
                </div>
              )}

              {job.error && <p className="text-xs text-red-600 mt-2 break-words">{job.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface PineconeDocument {
  fileName: string;
  pdfPath?: string;
  pageCount?: number;
  expectedChunks?: number;
  chunkCount: number;
  oeCodes: string[];
}

interface DocumentsSummary {
  totalDocuments: number;
  totalChunks: number;
  scannedAt: string;
}

const DOCUMENTS_PAGE_SIZE = 20;
const MAX_VISIBLE_OE_CODES = 6;

function DocumentsView() {
  const { accessToken } = useAuthStore();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<PineconeDocument[]>([]);
  const [summary, setSummary] = useState<DocumentsSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [chunks, setChunks] = useState<PineconeItem[]>([]);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async (targetPage: number, refresh = false) => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean> = { page: targetPage, limit: DOCUMENTS_PAGE_SIZE };
      if (search) params.search = search;
      if (refresh) params.refresh = true;

      const response = await axios.get(`${API_BASE_URL}/chatbot/pinecone-documents`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });
      const data = response.data.data;
      setItems(data.items || []);
      setSummary(data.summary || null);
      setTotal(data.total || 0);
      setPage(targetPage);
      setExpandedFile(null);
    } catch (err: any) {
      console.error('[PineconeData] Error loading Pinecone documents:', err);
      setError(
        err.response?.status === 403
          ? 'You do not have access to Pinecone data.'
          : err.response?.data?.error || 'Failed to load Pinecone documents.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, search]);

  useEffect(() => {
    loadDocuments(1);
  }, [loadDocuments]);

  const handleToggleFile = async (fileName: string) => {
    if (expandedFile === fileName) {
      setExpandedFile(null);
      return;
    }
    setExpandedFile(fileName);
    setChunks([]);
    setIsLoadingChunks(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/chatbot/pinecone-documents/chunks`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { fileName },
      });
      setChunks(response.data.data.items || []);
    } catch (err: any) {
      console.error('[PineconeData] Error loading document chunks:', err);
    } finally {
      setIsLoadingChunks(false);
    }
  };

  const handleDelete = async (doc: PineconeDocument) => {
    if (!accessToken || deletingFile) return;

    const confirmation = await Swal.fire({
      title: 'Delete from Pinecone?',
      html: `<b>${doc.fileName.replace(/[&<>"']/g, '')}</b><br/>${doc.chunkCount} chunks will be permanently removed. The chatbot will no longer use this document. To restore it, the PDF has to be uploaded again.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!confirmation.isConfirmed) return;

    setDeletingFile(doc.fileName);
    try {
      const response = await axios.delete(`${API_BASE_URL}/chatbot/pinecone-documents`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { fileName: doc.fileName },
      });
      Swal.fire({
        title: 'Deleted',
        text: response.data.data?.message || 'Document deleted from Pinecone.',
        icon: 'success',
        confirmButtonColor: '#1e73be',
      });
      // Stay on the current page unless its last row was just removed
      await loadDocuments(items.length === 1 && page > 1 ? page - 1 : page);
    } catch (err: any) {
      console.error('[PineconeData] Delete failed:', err);
      Swal.fire({
        title: 'Delete Failed',
        text: err.response?.data?.error || 'Could not delete the document from Pinecone.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setDeletingFile(null);
    }
  };

  const totalPages = Math.max(Math.ceil(total / DOCUMENTS_PAGE_SIZE), 1);

  return (
    <div>
      <UploadPanel onUploaded={() => loadDocuments(1)} />

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">File name or OE code</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="e.g. Bitcoin or 257.01"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-[#1e73be] hover:bg-[#1e73be]/90 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold transition-colors"
            >
              Clear
            </button>
          </div>
          <div className="flex items-end justify-end">
            <button
              type="button"
              onClick={() => loadDocuments(1, true)}
              disabled={isLoading}
              title="Rescan Pinecone instead of using the cached list"
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Rescan
            </button>
          </div>
        </form>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1">Files in Pinecone</p>
            <p className="text-2xl font-bold text-gray-800">{summary.totalDocuments.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1">Chunks with a file name</p>
            <p className="text-2xl font-bold text-gray-800">{summary.totalChunks.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1">Last scanned</p>
            <p className="text-sm font-bold text-gray-800">{new Date(summary.scannedAt).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">{error}</div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Scanning Pinecone... the first load can take up to a minute</span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Database className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No files found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">OE Codes</th>
                  <th className="px-4 py-3">Pages</th>
                  <th className="px-4 py-3">Chunks</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(doc => {
                  const isExpanded = expandedFile === doc.fileName;
                  const isIncomplete = typeof doc.expectedChunks === 'number' && doc.chunkCount < doc.expectedChunks;
                  return (
                    <Fragment key={doc.fileName}>
                      <tr
                        onClick={() => handleToggleFile(doc.fileName)}
                        className="border-b border-gray-100 hover:bg-gray-50 align-top cursor-pointer"
                      >
                        <td className="px-4 py-3 text-gray-400">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-3 text-gray-800 max-w-[420px]">
                          <p className="break-all font-medium">{doc.fileName}</p>
                          {doc.pdfPath && <div className="text-xs text-gray-400 break-all mt-0.5">{doc.pdfPath}</div>}
                        </td>
                        <td className="px-4 py-3 max-w-[280px]">
                          <div className="flex flex-wrap gap-1">
                            {doc.oeCodes.length === 0 && <span className="text-gray-400">—</span>}
                            {doc.oeCodes.slice(0, MAX_VISIBLE_OE_CODES).map(code => (
                              <span
                                key={code}
                                className="inline-flex px-2 py-0.5 rounded-md bg-orange-50 border border-orange-200 text-xs text-orange-700"
                              >
                                {code}
                              </span>
                            ))}
                            {doc.oeCodes.length > MAX_VISIBLE_OE_CODES && (
                              <span className="text-xs text-gray-400" title={doc.oeCodes.join(', ')}>
                                +{doc.oeCodes.length - MAX_VISIBLE_OE_CODES} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{doc.pageCount ?? '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-md border text-xs ${
                              isIncomplete
                                ? 'bg-red-50 border-red-200 text-red-700'
                                : 'bg-green-50 border-green-200 text-green-700'
                            }`}
                            title={isIncomplete ? 'Fewer chunks in Pinecone than the upload expected' : undefined}
                          >
                            {doc.chunkCount}{typeof doc.expectedChunks === 'number' ? ` / ${doc.expectedChunks}` : ''}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(doc); }}
                            disabled={deletingFile !== null}
                            title="Delete this document from Pinecone"
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {deletingFile === doc.fileName
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Trash2 className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <td colSpan={6} className="px-4 py-3">
                            {isLoadingChunks ? (
                              <div className="flex items-center gap-2 text-gray-500 text-xs">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading chunks...
                              </div>
                            ) : chunks.length === 0 ? (
                              <p className="text-xs text-gray-500">No chunks could be loaded for this file.</p>
                            ) : (
                              <div className="space-y-2">
                                {chunks.map(chunk => (
                                  <div key={chunk.id} className="bg-white border border-gray-200 rounded-lg p-3">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className="text-xs font-semibold text-gray-700">
                                        Chunk {typeof chunk.metadata.chunkIndex === 'number' ? chunk.metadata.chunkIndex + 1 : '—'}
                                      </span>
                                      {chunk.metadata.topic && (
                                        <span className="text-xs text-gray-600">{chunk.metadata.topic}</span>
                                      )}
                                      <span className="text-xs text-gray-400 font-mono break-all">{chunk.id}</span>
                                    </div>
                                    <p className="text-xs text-gray-600">{chunk.metadata.textPreview || '—'}</p>
                                    {typeof chunk.metadata.textLength === 'number' && (
                                      <div className="text-xs text-gray-400 mt-1">{chunk.metadata.textLength.toLocaleString()} chars</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!error && !isLoading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * DOCUMENTS_PAGE_SIZE + 1}–{Math.min(page * DOCUMENTS_PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadDocuments(page - 1)}
                disabled={page <= 1 || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => loadDocuments(page + 1)}
                disabled={page >= totalPages || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type PineconeView = 'documents' | 'vectors';

const VIEW_TABS: { value: PineconeView; label: string; icon: typeof FileText }[] = [
  { value: 'documents', label: 'Grouped by file', icon: FileText },
  { value: 'vectors', label: 'All vectors', icon: Layers },
];

export function PineconeData() {
  const [view, setView] = useState<PineconeView>('documents');

  return (
    <div className="notranslate" translate="no">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Pinecone Data"
          description="Metadata of the content indexed in Pinecone for the chatbot"
          breadcrumbs={[{ label: 'Pinecone Data' }]}
        />
        <div className="flex items-center gap-1 bg-white rounded-xl shadow-md border border-gray-100 p-1">
          {VIEW_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setView(tab.value)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                view === tab.value ? 'bg-[#1e73be] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'documents' ? <DocumentsView /> : <VectorsView />}
    </div>
  );
}

export default PineconeData;
